import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

export const LAST_CAMPAIGN_START = dayjs("2026-03-24");
export const LAST_CAMPAIGN_END = dayjs("2026-04-06");

export const PRESETS = {
  last24h: {
    label: "Last 24h",
    resolve: () => [dayjs().subtract(24, "hour"), dayjs()],
  },
  last7d: {
    label: "Last 7 days",
    resolve: () => [dayjs().subtract(7, "day"), dayjs()],
  },
  lastCampaign: {
    label: "Last campaign",
    resolve: () => [LAST_CAMPAIGN_START, LAST_CAMPAIGN_END],
  },
};

const TimeFilterContext = createContext(null);

export function TimeFilterProvider({ children }) {
  const [params, setParams] = useSearchParams();

  const preset = params.get("timePreset") || null;
  const rawStart = params.get("timeStart");
  const rawEnd = params.get("timeEnd");

  const mode = preset ? "historical" : "live";

  const dateRange = useMemo(() => {
    if (!preset) return null;
    if (preset === "custom") {
      if (!rawStart || !rawEnd) return null;
      const start = dayjs(rawStart);
      const end = dayjs(rawEnd);
      if (!start.isValid() || !end.isValid() || end.isBefore(start))
        return null;
      return [start, end];
    }
    return PRESETS[preset]?.resolve() ?? null;
  }, [preset, rawStart, rawEnd]);

  const setPreset = useCallback(
    (key) => {
      const next = new URLSearchParams(params);
      next.set("timePreset", key);
      next.delete("timeStart");
      next.delete("timeEnd");
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const setCustomRange = useCallback(
    (start, end) => {
      const next = new URLSearchParams(params);
      next.set("timePreset", "custom");
      next.set("timeStart", start.format("YYYY-MM-DD"));
      next.set("timeEnd", end.format("YYYY-MM-DD"));
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const returnToLive = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("timePreset");
    next.delete("timeStart");
    next.delete("timeEnd");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const value = useMemo(
    () => ({
      mode,
      preset,
      dateRange,
      setPreset,
      setCustomRange,
      returnToLive,
    }),
    [mode, preset, dateRange, setPreset, setCustomRange, returnToLive]
  );

  return (
    <TimeFilterContext.Provider value={value}>
      {children}
    </TimeFilterContext.Provider>
  );
}

export function useTimeFilter() {
  const ctx = useContext(TimeFilterContext);
  if (!ctx)
    throw new Error("useTimeFilter must be used within TimeFilterProvider");
  return ctx;
}
